import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import * as z from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { SearchIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import type { Entity } from "@/lib/queries/entities";
import type { ColumnDef } from "@tanstack/react-table";
import { Input } from "@/components/ui/input";
import { entitiesListQueryOptions } from "@/lib/queries/entities";
import { fuzzyFilter } from "@/lib/table/fuzzyFilter";
import { useDebouncedSearch } from "@/lib/hooks";
import {
  GenericPaginationLinks,
  GenericTable,
} from "@/components/generic-table";

const searchSchema = z.object({
  page: z.number().int().positive().catch(1),
  per_page: z.number().int().positive().max(100).catch(10),
  search: z.string().optional(),
});

export const Route = createFileRoute("/_app/entities/")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  search: {
    middlewares: [stripSearchParams({ search: "" })],
  },
  loaderDeps: ({ search }) => search,
  loader({ context: { queryClient }, deps }) {
    queryClient.ensureQueryData(entitiesListQueryOptions(deps));
  },
  staticData: {
    permissionGuards: [
      {
        resource: "entities",
      },
    ],
  },
});

const columns = [
  {
    accessorKey: "name",
    header: "Name",
    size: 400,
  },
  {
    accessorKey: "description",
    header: "Description",
    size: 400,
  },
] as const satisfies Array<ColumnDef<Entity>>;

function RouteComponent() {
  const searchParams = Route.useSearch();
  const [searchQuery, setSearchQuery, rawSearchQuery] = useDebouncedSearch(
    Route.id,
    "search",
  );
  const entities = useQuery(
    entitiesListQueryOptions({ ...searchParams, search: searchQuery }),
  );

  const table = useReactTable({
    data: entities.data?.items ?? [],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      pagination: {
        pageIndex: searchParams.page - 1,
        pageSize: searchParams.per_page,
      },
    },
    rowCount: entities.data?.count ?? 1,

    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Entities</h1>
      </div>

      <div className="relative mb-4 min-w-[220px] flex-1">
        <SearchIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search all columns..."
          value={rawSearchQuery ?? ""}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="rounded-md border">
        <GenericTable table={table} isLoading={entities.isLoading} />
      </div>

      <div className="mt-4">
        <GenericPaginationLinks table={table} />
      </div>
    </div>
  );
}
